/**
 * 消息系统 API 服务
 * 基于消息功能文档实现
 */
import { default as axios_instance } from "axios";
import { store } from "@redux/store";
import authActions from "@redux/auth/actions";
import { ENV } from "@config/env";

const { backendUrl } = ENV;

// 创建 axios 实例
const axios = axios_instance.create();

// 请求拦截器 - 添加认证 token
axios.interceptors.request.use(
  (config) => {
    const access = localStorage.getItem("access");
    if (access) {
      config.headers["Authorization"] = "Bearer " + access;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器 - 处理 token 刷新
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (!error.response) {
      return Promise.reject(error);
    }

    const originalRequest = error.config;
    const refresh = localStorage.getItem("refresh");
    const access = localStorage.getItem("access");

    if (
      error.response?.status === 401 &&
      originalRequest.url === backendUrl + `/auth/token/refresh`
    ) {
      if (error.response.data.code === "token_not_valid") {
        store.dispatch(authActions.logout(refresh, access));
      }
      return Promise.reject(error);
    } else if (
      refresh &&
      error.response?.status === 401 &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;
      try {
        const res = await axios.post(backendUrl + `/auth/token/refresh`, {
          refresh: refresh,
        });
        localStorage.setItem("access", res.data.access);
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + res.data.access;
        return axios(originalRequest);
      } catch (refreshError) {
        store.dispatch(authActions.logout(refresh, access));
        return Promise.reject(refreshError);
      }
    } else if (error.response?.status === 401) {
      store.dispatch(authActions.logout(refresh, access));
    }

    return Promise.reject(error);
  }
);

/**
 * 会话相关 API
 */
export const conversationApi = {
  /**
   * 获取会话列表
   * @param {Object} params - 查询参数
   * @param {number} params.page - 页码
   * @param {number} params.page_size - 每页数量
   * @returns {Promise}
   */
  getList: (params = {}) => {
    return axios.get(`${backendUrl}/api/v2/conversations/`, { params });
  },

  /**
   * 获取会话详情
   * @param {number} id - 会话ID
   * @returns {Promise}
   */
  getDetail: (id) => {
    return axios.get(`${backendUrl}/api/v2/conversations/${id}/`);
  },

  /**
   * 创建会话
   * @param {Object} data - 会话数据
   * @param {string} data.conversation_type - "private" 或 "group"
   * @param {string} data.business_type - 业务类型
   * @param {Array<number>} data.member_ids - 成员用户ID列表
   * @param {string} data.name - 群聊名称（可选）
   * @param {number} data.related_workorder - 关联工单ID（WORKORDER类型时）
   * @param {number} data.related_equipment - 关联设备ID（EQUIPMENT类型时）
   * @param {number} data.facility - 关联设施ID（可选）
   * @returns {Promise}
   */
  create: (data) => {
    return axios.post(`${backendUrl}/api/v2/conversations/`, data);
  },

  /**
   * 更新会话信息
   * @param {number} id - 会话ID
   * @param {Object} data - 更新数据
   * @returns {Promise}
   */
  update: (id, data) => {
    return axios.patch(`${backendUrl}/api/v2/conversations/${id}/`, data);
  },

  /**
   * 删除/退出会话
   * @param {number} id - 会话ID
   * @returns {Promise}
   */
  delete: (id) => {
    return axios.delete(`${backendUrl}/api/v2/conversations/${id}/`);
  },

  /**
   * 获取会话成员列表
   * @param {number} id - 会话ID
   * @returns {Promise}
   */
  getMembers: (id) => {
    return axios.get(`${backendUrl}/api/v2/conversations/${id}/members/`);
  },

  /**
   * 添加会话成员
   * @param {number} id - 会话ID
   * @param {Array<number>} userIds - 用户ID列表
   * @returns {Promise}
   */
  addMembers: (id, userIds) => {
    return axios.post(`${backendUrl}/api/v2/conversations/${id}/add_members/`, {
      user_ids: userIds,
    });
  },

  /**
   * 退出会话
   * @param {number} id - 会话ID
   * @param {number} userId - 用户ID（可选）
   * @returns {Promise}
   */
  removeMember: (id, userId = null) => {
    const data = userId ? { user_id: userId } : {};
    return axios.post(
      `${backendUrl}/api/v2/conversations/${id}/remove_member/`,
      data
    );
  },
};

/**
 * 文件上传相关 API
 */
export const fileUploadApi = {
  /**
   * 完整上传流程：获取签名 URL 并上传到 S3（POST 表单方式）
   * @param {number} conversationId - 会话 ID
   * @param {File} file - 文件对象
   * @returns {Promise<{fileKey: string}>} - 返回 fields.key，如 "messages/xxx.png"
   */
  uploadFile: async (conversationId, file) => {
    // 步骤 1: 获取签名 URL
    const response = await axios.post(
      `${backendUrl}/api/v2/messages/get_upload_url/`,
      {
        conversation_id: conversationId,
        filename: file.name,
      }
    );

    const { url, fields, file_url } = response.data;

    // 步骤 2: 上传文件到 S3（POST 表单方式）
    const formData = new FormData();

    // 添加签名返回的字段
    Object.entries(fields).forEach(([key, value]) => {
      formData.append(key, value);
    });

    // 添加文件（必须是最后一个字段）
    formData.append("file", file);

    const uploadResponse = await fetch(url, {
      method: "POST",
      body: formData,
    });

    if (!uploadResponse.ok) {
      throw new Error("文件上传失败");
    }

    // 返回 fields.key（传给后端），file_url 用于前端展示
    return { fileKey: fields.key, fileUrl: file_url };
  },
};

/**
 * 消息相关 API
 */
export const messageApi = {
  /**
   * 获取消息历史
   * @param {Object} params - 查询参数
   * @param {number} params.conversation_id - 会话ID（必填）
   * @param {number} params.after_id - 返回该消息ID之后的消息（增量获取）
   * @param {number} params.page - 页码
   * @param {number} params.page_size - 每页数量
   * @returns {Promise}
   */
  getList: (params) => {
    return axios.get(`${backendUrl}/api/v2/messages/`, { params });
  },

  /**
   * 发送消息
   * @param {number} conversationId - 会话ID
   * @param {Array<Object>} messages - 消息列表
   * @returns {Promise}
   */
  send: (conversationId, messages) => {
    return axios.post(`${backendUrl}/api/v2/messages/`, {
      conversation_id: conversationId,
      messages: messages,
    });
  },

  /**
   * 发送文本消息（便捷方法）
   * @param {number} conversationId - 会话ID
   * @param {string} content - 消息内容
   * @returns {Promise}
   */
  sendText: (conversationId, content) => {
    return messageApi.send(conversationId, [
      { message_type: "TEXT", content },
    ]);
  },

  /**
   * 发送图片消息（便捷方法）
   * @param {number} conversationId - 会话ID
   * @param {File} file - 图片文件
   * @param {string} content - 图片描述（可选）
   * @returns {Promise}
   */
  sendImage: (conversationId, file, content = "") => {
    return messageApi.send(
      conversationId,
      [{ message_type: "IMAGE", content }],
      { file: file }
    );
  },

  /**
   * 发送文件消息（便捷方法）
   * @param {number} conversationId - 会话ID
   * @param {File} file - 文件
   * @param {string} content - 文件描述（可选）
   * @returns {Promise}
   */
  sendFile: (conversationId, file, content = "") => {
    return messageApi.send(
      conversationId,
      [{ message_type: "FILE", content }],
      { file: file }
    );
  },

  /**
   * 上报已读位置
   * @param {number} conversationId - 会话ID
   * @param {number} lastReadMessageId - 最后已读消息ID
   * @returns {Promise}
   */
  reportRead: (conversationId, lastReadMessageId) => {
    return axios.post(`${backendUrl}/api/v2/messages/report_read/`, {
      conversation_id: conversationId,
      last_read_message_id: lastReadMessageId,
    });
  },
};

/**
 * 获取当前用户ID
 * @returns {number|null}
 */
export const getCurrentUserId = () => {
  return store.getState()?.Auth?.userId || null;
};

/**
 * 获取 WebSocket 基础 URL
 * @returns {string}
 */
export const getWebSocketBaseUrl = () => {
  // 将 http/https 替换为 ws/wss
  const wsProtocol = backendUrl.startsWith("https") ? "wss" : "ws";
  const wsHost = backendUrl.replace(/^https?:\/\//, "");
  return `${wsProtocol}://${wsHost}`;
};

/**
 * 获取或加入设备会话
 * @param {number} equipmentId - 设备ID
 * @param {string} equipmentName - 设备名称（用于创建会话时的名称）
 * @param {number} facilityId - 设施ID
 * @returns {Promise<Object>} 会话对象
 */
export const getOrJoinEquipmentConversation = async (equipmentId, equipmentName, facilityId, member_ids) => {
  try {
    // 直接创建会话，后端会自动处理：已存在则返回，不存在则创建并自动加入当前用户
    const response = await conversationApi.create({
      conversation_type: "group",
      business_type: "EQUIPMENT",
      related_equipment: equipmentId,
      facility: facilityId,
      name: `Equipment: ${equipmentName}`,
      member_ids
    });

    return response.data;
  } catch (error) {
    console.error("Failed to get or join equipment conversation:", error);
    throw error;
  }
};

export default {
  conversation: conversationApi,
  message: messageApi,
  fileUpload: fileUploadApi,
  getCurrentUserId,
  getWebSocketBaseUrl,
  getOrJoinEquipmentConversation,
};
